(() => {
  if (customElements.get('csb-burn-comparison')) return;

  class CsbBurnComparison extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.frame = this.querySelector('[data-burn-frame]');
      this.after = this.querySelector('[data-burn-after]');
      this.line = this.querySelector('[data-burn-line]');
      this.handle = this.querySelector('[data-burn-handle]');
      this.range = this.querySelector('[data-burn-range]');
      this.beforeLabel = this.querySelector('[data-burn-before-label]');
      this.afterLabel = this.querySelector('[data-burn-after-label]');
      this.playButton = this.querySelector('[data-burn-play]');
      this.playText = this.querySelector('[data-burn-play-text]');
      this.progressText = this.querySelector('[data-burn-progress]');
      this.photos = [...this.querySelectorAll('.csb-burn__image img')];
      this.ash = this.querySelector('.csb-burn__ash');
      if (!this.frame || !this.after || !this.line || !this.handle || !this.range) return;
      this.controller = new AbortController();
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = true;
      this.playing = false;
      this.completed = false;
      this.tourTime = 0;
      this.tourStarted = false;
      this.camera = { scale: 1, x: 0, y: 0, yaw: 0 };
      this.raf = 0;
      this.lastTime = null;
      const signal = this.controller.signal;
      this.range.addEventListener('input', () => {
        this.takeControl();
        this.render(Number(this.range.value));
        this.updatePlayButton();
      }, { signal });
      this.range.addEventListener('focus', () => this.pause(), { signal });
      this.range.addEventListener('pointerdown', () => this.pause(), { signal });
      this.playButton?.addEventListener('click', () => this.togglePlay(), { signal });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') this.pause();
      }, { signal });
      this.frame.addEventListener('pointerdown', event => this.pointerStart(event), { signal });
      this.frame.addEventListener('pointermove', event => this.pointerMove(event), { signal });
      this.frame.addEventListener('pointerup', event => this.pointerEnd(event), { signal });
      this.frame.addEventListener('pointercancel', () => { this.pointer = null; }, { signal });
      this.frame.addEventListener('lostpointercapture', () => { this.pointer = null; }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause();
      }, { signal });
      this.reduced.addEventListener('change', () => {
        this.takeControl();
        this.renderCamera({ scale: 1, x: 0, y: 0, yaw: 0 });
        this.render(this.amount);
      }, { signal });
      this.measure = () => {
        const rect = this.frame.getBoundingClientRect();
        this.cameraUnit = Math.min(rect.width, rect.height) || 1;
        this.renderCamera(this.camera);
      };
      this.measure();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.measure);
        this.resizeObserver.observe(this.frame);
      } else {
        window.addEventListener('resize', this.measure, { signal });
      }
      this.render(Number(this.range.value) || 0);
      this.updatePlayButton();
      this.dataset.ready = 'true';
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            this.visible = entry.isIntersecting;
            if (!this.visible) this.pause();
          });
        }, { threshold: 0 });
        this.observer.observe(this.frame);
      }
    }

    disconnectedCallback() {
      this.pause();
      this.controller?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.controller = null;
      this.observer = null;
      this.resizeObserver = null;
      this.pointer = null;
    }

    render(value) {
      const amount = Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));
      this.amount = amount;
      const center = 112 - amount * 1.24;
      this.after.style.clipPath = `polygon(${center + 12}% 0,100% 0,100% 100%,${center - 12}% 100%)`;
      this.line.setAttribute('x1', String((center + 12) * 10));
      this.line.setAttribute('x2', String((center - 12) * 10));
      this.handle.style.left = `${center}%`;
      this.handle.style.opacity = center < 3 || center > 97 ? '0' : '1';
      if (this.beforeLabel) this.beforeLabel.style.opacity = String(Math.min(1, (100 - amount) / 12));
      if (this.afterLabel) this.afterLabel.style.opacity = String(Math.min(1, amount / 12));
      const rounded = String(Math.round(amount));
      this.range.value = rounded;
      this.range.style.setProperty('--burn-fill', `${amount}%`);
      this.range.setAttribute('aria-valuetext', (this.dataset.valueTemplate || '__AMOUNT__%').replace('__AMOUNT__', rounded));
      this.dataset.reveal = rounded;
      if (this.progressText) this.progressText.textContent = `${rounded}%`;
    }

    updatePlayButton() {
      this.dataset.playing = String(Boolean(this.playing));
      if (!this.playButton) return;
      const labels = this.playButton.dataset;
      const label = this.playing
        ? (labels.pauseLabel || 'Pause comparison')
        : this.completed
          ? (labels.replayLabel || 'Replay comparison')
          : (labels.playLabel || 'Play comparison');
      (this.playText || this.playButton).textContent = label;
      this.playButton.setAttribute('aria-pressed', String(Boolean(this.playing)));
      this.playButton.setAttribute('aria-label', label);
    }

    renderCamera(camera) {
      this.camera = { ...camera };
      const unit = this.cameraUnit || 1;
      const transform = `perspective(1000px) translate3d(${camera.x * unit}px,${camera.y * unit}px,0) rotateY(${camera.yaw}deg) scale(${camera.scale})`;
      // Clip the stationary outer wrapper, never the moving photograph. Both
      // originals share precisely the same camera, so scrubbing stays aligned.
      this.photos.forEach(photo => { photo.style.transform = transform; });
      if (this.ash) {
        const depth = (camera.scale - 1) / 1.15;
        this.ash.style.transform = `translate3d(${-camera.x * unit * .13}px,${camera.y * unit * .09}px,0) scale(${1 + depth * .18})`;
      }
      this.dataset.cameraScale = camera.scale.toFixed(3);
    }

    takeControl() {
      this.pause();
      this.completed = false;
      this.tourStarted = false;
      this.tourTime = 0;
      this.updatePlayButton();
    }

    renderTour(milliseconds) {
      const time = milliseconds / 1000;
      const ease = (start, end) => {
        const t = Math.min(1, Math.max(0, (time - start) / (end - start)));
        return t * t * (3 - 2 * t);
      };
      const blend = (a, b, t) => Object.fromEntries(Object.keys(a).map(key => [key, a[key] + (b[key] - a[key]) * t]));
      const home = { scale: 1, x: 0, y: 0, yaw: 0 };
      const top = { scale: 2.15, x: -.025, y: .40, yaw: -5 };
      const bottom = { scale: 2.15, x: .04, y: -.33, yaw: 6 };
      let camera;
      if (time < 3.2) camera = blend(this.startCamera, top, ease(1, 3.2));
      else if (time < 8.4) camera = blend(top, bottom, ease(3.2, 7.5));
      else camera = blend(bottom, home, ease(8.4, 10.8));
      this.renderCamera(camera);
      const reveal = time < 1.5
        ? this.startAmount + (100 - this.startAmount) * ease(0, 1.5)
        : 100 - 50 * ease(10.8, 12);
      this.render(reveal);
      this.dataset.tourPhase = time < 1.5 ? 'reveal' : time < 3.2 ? 'closeup' : time < 8.4 ? 'inspect' : time < 10.8 ? 'return' : 'centre';
    }

    pause() {
      this.playing = false;
      cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.lastTime = null;
      this.updatePlayButton();
    }

    togglePlay() {
      if (this.playing) {
        this.pause();
        return;
      }
      if (!this.visible || document.hidden) return;
      if (this.reduced.matches) {
        this.render(50);
        this.renderCamera({ scale: 1, x: 0, y: 0, yaw: 0 });
        this.completed = true;
        this.tourStarted = false;
        this.updatePlayButton();
        return;
      }
      if (!this.tourStarted || this.completed) {
        this.tourTime = 0;
        this.startAmount = this.amount;
        this.startCamera = { ...this.camera };
        this.tourStarted = true;
        // Request sharper variants only when someone chooses the closeup.
        this.photos.forEach(photo => { photo.sizes = `${Math.ceil(this.cameraUnit * 2.15)}px`; });
      }
      this.completed = false;
      this.playing = true;
      this.lastTime = null;
      this.updatePlayButton();
      this.raf = requestAnimationFrame(time => this.tick(time));
    }

    tick(time) {
      this.raf = 0;
      if (!this.playing || !this.isConnected || document.hidden || !this.visible) {
        this.pause();
        return;
      }
      const elapsed = this.lastTime === null ? 0 : Math.min(100, Math.max(0, time - this.lastTime));
      this.lastTime = time;
      this.tourTime = Math.min(12000, this.tourTime + elapsed);
      this.renderTour(this.tourTime);
      if (this.tourTime >= 12000) {
        this.render(50);
        this.renderCamera({ scale: 1, x: 0, y: 0, yaw: 0 });
        this.completed = true;
        this.tourStarted = false;
        this.pause();
        return;
      }
      this.raf = requestAnimationFrame(nextTime => this.tick(nextTime));
    }

    pointerStart(event) {
      if (!event.isPrimary || event.button !== 0) return;
      this.pause();
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false, rejected: false };
      this.frame.setPointerCapture(event.pointerId);
    }

    pointerMove(event) {
      const pointer = this.pointer;
      if (!pointer || pointer.id !== event.pointerId || pointer.rejected) return;
      const dx = Math.abs(event.clientX - pointer.x);
      const dy = Math.abs(event.clientY - pointer.y);
      if (!pointer.moved && dy > 8 && dy > dx) {
        pointer.rejected = true;
        return;
      }
      if (!pointer.moved && dx < 5) return;
      pointer.moved = true;
      this.pointerPosition(event.clientX);
    }

    pointerEnd(event) {
      const pointer = this.pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      if (!pointer.rejected) this.pointerPosition(event.clientX);
      this.pointer = null;
      if (this.frame.hasPointerCapture(event.pointerId)) this.frame.releasePointerCapture(event.pointerId);
    }

    pointerPosition(clientX) {
      const rect = this.frame.getBoundingClientRect();
      if (!rect.width) return;
      const x = Math.min(100, Math.max(0, (clientX - rect.left) / rect.width * 100));
      this.takeControl();
      this.render(x <= 2 ? 100 : x >= 98 ? 0 : (112 - x) / 1.24);
      this.updatePlayButton();
    }
  }

  customElements.define('csb-burn-comparison', CsbBurnComparison);
})();
